import styled from 'styled-components'

export const Container = styled.main`
  width: 100%;
  min-height: 100vh;
  padding-top: 70px;
  background: #f8f8f7;
  color: #333;
`

export const Hero = styled.section`
  padding: 58px 24px 46px;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
`

export const HeroInner = styled.div`
  width: min(1240px, 100%);
  margin: 0 auto;
`

export const Eyebrow = styled.div`
  margin-bottom: 8px;
  color: #e00000;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

export const HeroTitle = styled.h1`
  max-width: 820px;
  margin: 0;
  color: #292929;
  font-size: clamp(34px, 4vw, 50px);
  line-height: 1.06;

  strong {
    color: #e00000;
  }
`

export const HeroSubtitle = styled.p`
  max-width: 800px;
  margin: 15px 0 0;
  color: #666;
  font-size: 17px;
  line-height: 1.65;
`

export const Content = styled.section`
  width: min(1240px, calc(100% - 48px));
  margin: 0 auto;
  padding: 38px 0 68px;

  @media (max-width: 720px) {
    width: calc(100% - 28px);
    padding-top: 28px;
  }
`

export const SectionIntro = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 22px;

  @media (max-width: 820px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

export const SectionTitle = styled.h2`
  margin: 0;
  color: #303030;
  font-size: 24px;
  line-height: 1.2;
`

export const SectionDescription = styled.p`
  max-width: 700px;
  margin: 7px 0 0;
  color: #777;
  font-size: 14px;
  line-height: 1.55;
`

export const FlowHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 9px 13px;
  border: 1px solid #e6e2de;
  border-radius: 999px;
  background: #fff;
  color: #777;
  font-size: 12px;

  svg {
    color: #b7b7b7;
    font-size: 10px;
  }

  strong {
    color: #e00000;
  }
`

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`

export const Card = styled.article`
  overflow: hidden;
  display: flex;
  min-width: 0;
  flex-direction: column;
  border: 1px solid #e9e6e2;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.045);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 13px 30px rgba(0, 0, 0, 0.08);
  }
`

type CardImageProps = { $image: string }

export const CardImage = styled.div<CardImageProps>`
  position: relative;
  height: 180px;
  background-color: #e9e9e9;
  background-image: ${({ $image }) =>
    `linear-gradient(180deg, rgba(0,0,0,0.03), rgba(0,0,0,0.15)), url("${$image}")`};
  background-position: center;
  background-size: cover;
`

export const PartnerBadge = styled.span`
  position: absolute;
  top: 13px;
  left: 13px;
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: #3d3d3d;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
`

export const CardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 19px 20px 20px;
`

export const CardTitle = styled.h3`
  margin: 0;
  color: #2f2f2f;
  font-size: 20px;
  line-height: 1.25;
`

export const CardDescription = styled.p`
  margin: 9px 0 0;
  color: #686868;
  font-size: 13px;
  line-height: 1.55;
`

export const Highlights = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0 20px;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #5b5b5b;
    font-size: 12px;
    line-height: 1.4;
  }

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: #e00000;
    font-size: 11px;
  }
`

export const PartnerButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  margin-top: auto;
  padding: 0 15px;
  border: 1px solid #e00000;
  border-radius: 999px;
  background: #fff;
  color: #e00000;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;

  svg {
    font-size: 11px;
  }

  &:hover {
    background: #e00000;
    color: #fff;
    transform: translateY(-1px);
  }
`

export const Disclosure = styled.p`
  max-width: 780px;
  margin: 28px auto 0;
  color: #969696;
  font-size: 11px;
  line-height: 1.55;
  text-align: center;
`
